import React, { useState, useEffect, useContext } from "react";
import { Link, useLocation } from "react-router-dom";
import {
	getSettlementCycleDetail,
	formatNumber,
	getSettlementItems,
	getItemTypeText,
	getItemStatusText,
	getItemStatusColor,
	createSettlementAdjustment,
	uploadFiles,
	getSettlementAdjustments,
	rejectAdjustment,
	cancelAdjustment,
	confirmAdjustment,
	getAdjustmentDetail,
	createBooking,
	confirmBooking,
	rejectBooking,
	SettlementAdjustment,
} from "../../../services/SettlementService";
import { getAllAdjustmentReasonCodes } from "../../../constants/adjustmentReasonCode";
import { AuthenticationContext } from "../../controls/auth/AuthenticationWrap";
import { MyBookingWrapper, PaginationWrapper, PaginationWrapperTop } from "../MyBooking/styled/DesStyled";
import { OrderInner } from "../Order/styled/OrderStyle";
import NumberPaginationBox from "../../controls/components/numberPaginationBox/NumberPaginationBox";
import ProtectedRoute from "../../controls/auth/ProtectedRoute";
import { UserRole } from "../../../constants/roles";
import AdjustmentDetailModal from "./components/AdjustmentDetailModal";
import CancelConfirmModal from "../DebtManagement/components/CancelConfirmModal";
import AddAdjustmentModal from "./components/AddAdjustmentModal";
import AddBookingModal from "./components/AddBookingModal";
import BookingDetailModal from "./components/BookingDetailModal";
import AddAdjustmentSuccessModal from "./components/AddAdjustmentSuccessModal";
import AddBookingSuccessModal from "./components/AddBookingSuccessModal";
import ApproveAdjustmentSuccessModal from "./components/ApproveAdjustmentSuccessModal";
import ApproveConfirmModal from "./components/ApproveConfirmModal";
import RejectBookingModal from "./components/RejectBookingModal";
import RejectBookingSuccessModal from "./components/RejectBookingSuccessModal";
import RejectAdjustmentModal from "./components/RejectAdjustmentModal";
import AdjustmentProposalModal from "./components/AdjustmentProposalModal";
import useAttachAlertDialog, { AlertType } from "../../hooks/common/useAttachAlertDialog";
import CommonAlertDialog from "../../controls/components/commonAlertDialog/CommonAlertDialog";
import {
	DebtDetailBreadcrumb,
	BreadcrumbItem,
	BreadcrumbSeparator,
	DebtDetailContainer,
	DebtDetailMainTitle,
	DebtDetailHeader,
	DebtDetailTitle,
	DebtDetailSubtitle,
	DebtDetailSentButton,
	DebtDetailSummaryCards,
	DebtDetailCard,
	DebtDetailCardIcon,
	DebtDetailCardContent,
	DebtDetailCardText,
	DebtDetailCardValue,
	DebtDetailSection,
	DebtDetailSectionHeader,
	DebtDetailSectionTitle,
	DebtDetailSectionActions,
	DebtDetailFilterRow,
	DebtDetailSearchInput,
	DebtDetailSelect,
	DebtDetailAddButton,
	DebtDetailTable,
	DebtDetailTableHeader,
	DebtDetailTableRow,
	DebtDetailTableCell,
	DebtDetailStatusBadge,
	DebtDetailActionButton,
	DebtDetailDetailsLink,
} from "./styled/DebtDetailStyles";
import PermissionGate from "../../controls/auth/PermissionGate";

export default function DebtDetail() {
	return (
		<ProtectedRoute
			allowedRoles={[UserRole.Admin]}
			fallbackTitle="Access Denied"
			fallbackMessage="This page is only accessible to administrators.">
			<DebtDetailContent />
		</ProtectedRoute>
	);
}

function DebtDetailContent() {
	const authContext = useContext(AuthenticationContext);
	const { userInfo } = authContext || {};
	const location = useLocation();
	const [cycleDetail, setCycleDetail] = useState<any>(null);
	const [loading, setLoading] = useState(true);
	const [bookingItems, setBookingItems] = useState<any[]>([]);
	const [bookingLoading, setBookingLoading] = useState(false);
	const [bookingTotal, setBookingTotal] = useState(0);
	const [bookingPages, setBookingPages] = useState(0);
	const [bookingPage, setBookingPage] = useState(1);
	const [bookingSortField, setBookingSortField] = useState<string | undefined>("id");
	const [bookingSortOrder, setBookingSortOrder] = useState<"ASC" | "DESC">("DESC");
	const [searchTerm, setSearchTerm] = useState("");
	const [selectedReason, setSelectedReason] = useState("all");
	const [selectedStatus, setSelectedStatus] = useState("all");
	const [selectedItems, setSelectedItems] = useState<number[]>([]);
	const [isSelectAll, setIsSelectAll] = useState(false);
	const [isAdjustmentModalOpen, setIsAdjustmentModalOpen] = useState(false);
	const [selectedAdjustment, setSelectedAdjustment] = useState<SettlementAdjustment | null>(null);
	const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
	const [cancelItemId, setCancelItemId] = useState<number | null>(null);
	const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
	const [isProposalModalOpen, setIsProposalModalOpen] = useState(false);
	const [rejectItemId, setRejectItemId] = useState<number | null>(null);
	const [rejectData, setRejectData] = useState<{ amount: string; reason: string } | null>(null);
	const [isAddAdjustmentModalOpen, setIsAddAdjustmentModalOpen] = useState(false);
	const [isAddBookingModalOpen, setIsAddBookingModalOpen] = useState(false);
	const [isBookingDetailModalOpen, setIsBookingDetailModalOpen] = useState(false);
	const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
	const [isBookingSuccessModalOpen, setIsBookingSuccessModalOpen] = useState(false);
	const [isApproveSuccessModalOpen, setIsApproveSuccessModalOpen] = useState(false);
	const [isApproveConfirmModalOpen, setIsApproveConfirmModalOpen] = useState(false);
	const [pendingAdjustmentId, setPendingAdjustmentId] = useState<number | null>(null);
	const [pendingBookingId, setPendingBookingId] = useState<string | null>(null);
	const [isRejectBookingModalOpen, setIsRejectBookingModalOpen] = useState(false);
	const [isRejectBookingSuccessModalOpen, setIsRejectBookingSuccessModalOpen] = useState(false);
	const [pendingRejectBookingId, setPendingRejectBookingId] = useState<string | null>(null);
	const [selectedBookingId, setSelectedBookingId] = useState<string | null>(null);
	const [selectedBookingItemId, setSelectedBookingItemId] = useState<number | null>(null);
	const [selectedBookingItem, setSelectedBookingItem] = useState<any | null>(null);
	const [rawBookingItems, setRawBookingItems] = useState<any[]>([]);
	const [adjustmentData, setAdjustmentData] = useState<SettlementAdjustment[]>([]);
	const [adjustmentLoading, setAdjustmentLoading] = useState(false);
	const [adjustmentTotal, setAdjustmentTotal] = useState(0);
	const [adjustmentPages, setAdjustmentPages] = useState(0);
	const [adjustmentPage, setAdjustmentPage] = useState(1);
	const [adjustmentSortField, setAdjustmentSortField] = useState<string | undefined>(undefined);
	const [adjustmentSortOrder, setAdjustmentSortOrder] = useState<"ASC" | "DESC">("DESC");
	const [hoveredCardIndex, setHoveredCardIndex] = useState<number | null>(null);
	const { dialogState, openAlertDialog, closeAlertDialog } = useAttachAlertDialog({});

	const searchParams = new URLSearchParams(location.search);
	const cycleId = searchParams.get("cycleId") ? parseInt(searchParams.get("cycleId")!) : null;

	const formatDate = (dateString: string) => {
		if (!dateString) return "N/A";
		const date = new Date(dateString);
		const day = String(date.getDate()).padStart(2, "0");
		const month = String(date.getMonth() + 1).padStart(2, "0");
		const hours = String(date.getHours()).padStart(2, "0");
		const minutes = String(date.getMinutes()).padStart(2, "0");
		return `${day}/${month} ${hours}:${minutes}`;
	};

	const handleBookingSort = (field: string) => {
		if (bookingSortField === field) {
			setBookingSortOrder(bookingSortOrder === "ASC" ? "DESC" : "ASC");
		} else {
			setBookingSortField(field);
			setBookingSortOrder("ASC");
		}
	};

	useEffect(() => {
		const loadCycleDetail = async () => {
			if (!cycleId) {
				setLoading(false);
				return;
			}
			try {
				setLoading(true);
				const data = await getSettlementCycleDetail(cycleId);
				setCycleDetail(data);
			} catch (error) {
				console.error("Error loading cycle detail:", error);
			} finally {
				setLoading(false);
			}
		};
		loadCycleDetail();
	}, [cycleId]);

	useEffect(() => {
		if (cycleId) {
			setBookingPage(1);
		}
	}, [cycleId]);

	useEffect(() => {
		const loadBookingItems = async () => {
			if (!cycleId) {
				setBookingItems([]);
				return;
			}
			try {
				setBookingLoading(true);

				// Luôn dùng page = 1 khi có searchTerm, và cập nhật state nếu cần
				const pageToUse = searchTerm ? 1 : bookingPage;
				if (searchTerm && bookingPage !== 1) {
					setBookingPage(1);
				}

				// Mặc định sort theo id DESC (mới nhất trước)
				const sort: any = {};
				if (bookingSortField) {
					sort[bookingSortField] = bookingSortOrder;
				} else {
					// Nếu không có field được chọn, mặc định sort theo id DESC
					sort.id = "DESC";
				}

				const response = await getSettlementItems(
					pageToUse,
					10,
					cycleId,
					undefined,
					undefined,
					selectedStatus === "all" ? undefined : parseInt(selectedStatus),
					searchTerm || undefined,
					sort
				);

				const mappedItems = response.data.map((item: any) => {
					const statusColors = getItemStatusColor(item.status);
					return {
						id: item.id,
						booking: String(item.id),
						customer: item.customerName,
						email: item.customerEmail,
						dropoff: formatDate(item.timeIn),
						pickup: formatDate(item.timeOut),
						qty: String(item.quantity),
						income: formatNumber(item.finalAmount),
						type: getItemTypeText(item.type),
						typeBg: "#DBEAFE",
						typeColor: "#1E40AF",
						status: getItemStatusText(item.status),
						statusBg: statusColors.bg,
						statusColor: statusColors.text,
						orderId: item.orderId,
					};
				});

				setBookingItems(mappedItems);
				setBookingTotal(response.total);
				setBookingPages(response.pages);
			} catch (error) {
				console.error("Error loading booking items:", error);
				setBookingItems([]);
			} finally {
				setBookingLoading(false);
			}
		};

		loadBookingItems();
	}, [cycleId, bookingPage, searchTerm, selectedStatus, bookingSortField, bookingSortOrder]);

	const formatCloseDate = (dateString: string | null) => {
		if (!dateString) return "N/A";
		const date = new Date(dateString);
		const day = String(date.getDate()).padStart(2, "0");
		const month = String(date.getMonth() + 1).padStart(2, "0");
		const year = date.getFullYear().toString().slice(-2);
		const hours = String(date.getHours()).padStart(2, "0");
		const minutes = String(date.getMinutes()).padStart(2, "0");
		return `${day}/${month}/${year} ${hours}:${minutes}`;
	};

	const cycleCurrency = cycleDetail?.currency || "USD";

	const summaryData = cycleDetail
		? [
				{
					title: "Tổng thu nhập",
					value: `${formatNumber(cycleDetail.totalIncome)} ${cycleCurrency}`,
					valueColor: "#10B981",
					iconBg: "#E5E7EB",
					icon: "./static/img/debt/ic-detail1.png",
					iconHover: "./static/img/debt/ic-detail1-h.png",
				},
				{
					title: "Số booking",
					value: String(cycleDetail.itemCount || 0),
					valueColor: "#005E9E",
					iconBg: "#005E9E",
					icon: "./static/img/debt/ic-detail2.png",
					iconHover: "./static/img/debt/ic-detail2-h.png",
				},
				{
					title: "Số hành lý",
					value: String(cycleDetail.totalQuantity || 0),
					valueColor: "#374151",
					iconBg: "#E5E7EB",
					icon: "./static/img/debt/ic-detail3.png",
					iconHover: "./static/img/debt/ic-detail3-h.png",
				},
				{
					title: "Hạn xác nhận",
					value: formatCloseDate(cycleDetail.closeAt),
					valueColor: "#FF0000",
					iconBg: "#E5E7EB",
					icon: "./static/img/debt/ic-detail4.png",
					iconHover: "./static/img/debt/ic-detail4-h.png",
				},
		  ]
		: [];

	const fetchAdjustments = async () => {
		if (!cycleId) return;

		setAdjustmentLoading(true);
		try {
			const statusMap: { [key: string]: number } = {
				pending: 10,
				approved: 20,
				rejected: 40,
			};

			const sort: any = adjustmentSortField
				? {
						[adjustmentSortField]: adjustmentSortOrder,
				  }
				: { id: "DESC" as const };

			const result = await getSettlementAdjustments(
				adjustmentPage,
				10,
				cycleId,
				selectedStatus !== "all" ? statusMap[selectedStatus] : undefined,
				selectedReason !== "all" ? selectedReason : undefined,
				sort
			);

			setAdjustmentData(result.data);
			setAdjustmentTotal(result.total);
			setAdjustmentPages(result.pages);
		} catch (error) {
			console.error("Error fetching adjustments:", error);
		} finally {
			setAdjustmentLoading(false);
		}
	};

	useEffect(() => {
		if (cycleId) {
			setAdjustmentPage(1);
		}
	}, [cycleId]);

	useEffect(() => {
		fetchAdjustments();
	}, [cycleId, adjustmentPage, selectedStatus, selectedReason, adjustmentSortField, adjustmentSortOrder]);

	const getAdjustmentSource = (adjustment: SettlementAdjustment): { name: string; color: string } => {
		// Role: 1 = Admin, 2 = Partner, 3 = Customer
		if (adjustment.proposer?.role === 1) {
			return { name: "Whalelo", color: "#3B82F6" };
		} else if (adjustment.proposer?.role === 2) {
			return { name: "Station", color: "#10B981" };
		}
		return { name: "System", color: "#F59E0B" };
	};

	const getReviewerInfo = (adjustment: SettlementAdjustment): { name: string; role: string } | null => {
		if (!adjustment.reviewer) return null;

		const roleName =
			adjustment.reviewer.role === 1 ? "Admin" : adjustment.reviewer.role === 2 ? "Station" : "System";
		const reviewerName = adjustment.reviewer.fullName || `User ${adjustment.reviewer.id}`;

		return {
			name: reviewerName,
			role: roleName,
		};
	};

	const getAdjustmentStatusInfo = (status: number): { text: string; bg: string; color: string } => {
		switch (status) {
			case 10: // Pending
				return { text: "Chờ duyệt", bg: "#FEF3C7", color: "#B45309" };
			case 20: // Confirm
				return { text: "Đã duyệt", bg: "#ECFDF3", color: "#059669" };
			case 30: // Cancel
				return { text: "Đã hủy", bg: "#FEE2E2", color: "#DC2626" };
			case 40: // Reject
				return { text: "Đã từ chối", bg: "#FEE2E2", color: "#DC2626" };
			default:
				return { text: "Unknown", bg: "#E5E7EB", color: "#6B7280" };
		}
	};

	const formatAdjustmentTime = (dateString: string) => {
		if (!dateString) return "N/A";
		const date = new Date(dateString);
		const day = String(date.getDate()).padStart(2, "0");
		const month = String(date.getMonth() + 1).padStart(2, "0");
		const year = String(date.getFullYear()).slice(-2);
		const hours = String(date.getHours()).padStart(2, "0");
		const minutes = String(date.getMinutes()).padStart(2, "0");
		return `${day}/${month}/${year} ${hours}:${minutes}`;
	};

	const handleAdjustmentSort = (field: string) => {
		if (adjustmentSortField === field) {
			setAdjustmentSortOrder(adjustmentSortOrder === "ASC" ? "DESC" : "ASC");
		} else {
			setAdjustmentSortField(field);
			setAdjustmentSortOrder("DESC");
		}
	};

	const handleApproveAdjustmentDirect = (adjustmentId: number) => {
		setPendingAdjustmentId(adjustmentId);
		setIsApproveConfirmModalOpen(true);
	};

	const handleConfirmApproveAdjustment = async (adjustmentId: number) => {
		try {
			await confirmAdjustment(adjustmentId);

			// Reload adjustments và cycle detail
			if (cycleId) {
				const updatedDetail = await getSettlementCycleDetail(cycleId);
				setCycleDetail(updatedDetail);
				await fetchAdjustments();
			}

			// Mở popup thành công
			setIsApproveSuccessModalOpen(true);
		} catch (error) {
			console.error("Error approving adjustment:", error);
			// Mở popup lỗi
			openAlertDialog(
				AlertType.Fail,
				"Có lỗi xảy ra khi duyệt điều chỉnh. Vui lòng thử lại.",
				undefined,
				undefined,
				undefined,
				{ title: "Lỗi" }
			);
		}
	};

	const handleRejectAdjustmentDirect = async (adjustmentId: number) => {
		// Tìm adjustment để lấy amount
		const adjustment = adjustmentData.find((adj) => adj.id === adjustmentId);
		if (!adjustment) return;

		const valuePrefix = adjustment.type === 10 ? "+" : "-";
		const amount = `${valuePrefix}${formatNumber(adjustment.amount)} ${cycleCurrency}`;

		setRejectItemId(adjustmentId);
		setRejectData({ amount, reason: "" });
		setIsRejectModalOpen(true);
	};

	const handleRejectNext = (data: { amount: string; reason: string }) => {
		setRejectData(data);
		setIsRejectModalOpen(false);
		setIsProposalModalOpen(true);
	};

	const handleProposalBack = () => {
		setIsProposalModalOpen(false);
		setIsRejectModalOpen(true);
	};

	const handleRejectAndPropose = async (data: any) => {
		if (!rejectItemId || !cycleId || !userInfo?.id) {
			alert("Thiếu thông tin. Vui lòng thử lại.");
			return;
		}

		try {
			// Bước 1: Reject điều chỉnh cũ
			await rejectAdjustment(rejectItemId, data.rejectReason);

			// Bước 2: Tạo điều chỉnh mới
			const adjustmentType = parseInt(data.adjustmentType) || 10;
			let attachments: string[] = [];

			if (data.files && Array.isArray(data.files) && data.files.length > 0) {
				try {
					const uploadedUrls = await uploadFiles(data.files);
					attachments = uploadedUrls;
				} catch (uploadError) {
					console.error("Error uploading files:", uploadError);
					alert("Có lỗi xảy ra khi upload file. Điều chỉnh đã được từ chối nhưng file chưa được upload.");
				}
			}

			const adjustmentData = {
				createdBy: typeof userInfo.id === "string" ? parseInt(userInfo.id) : userInfo.id,
				proposedBy: typeof userInfo.id === "string" ? parseInt(userInfo.id) : userInfo.id,
				type: adjustmentType,
				amount: parseFloat(data.adjustmentAmount) || 0,
				reasonCode: data.reasonCode.trim(),
				description: data.description || "",
				attachments: attachments,
			};

			await createSettlementAdjustment(cycleId, adjustmentData);

			// Reload adjustments và cycle detail
			const updatedDetail = await getSettlementCycleDetail(cycleId);
			setCycleDetail(updatedDetail);
			await fetchAdjustments();

			// Close modals
			setIsProposalModalOpen(false);
			setRejectItemId(null);
			setRejectData(null);

			alert("Từ chối và đề xuất điều chỉnh mới thành công!");
		} catch (error) {
			console.error("Error rejecting and proposing adjustment:", error);
			alert("Có lỗi xảy ra khi từ chối và đề xuất điều chỉnh mới. Vui lòng thử lại.");
		}
	};

	const getAdjustmentAction = (
		adjustment: SettlementAdjustment
	): Array<{ text: string; color: string; onClick: () => void }> => {
		const actions: Array<{ text: string; color: string; onClick: () => void }> = [];

		if (adjustment.status === 10 && userInfo?.id) {
			// Pending
			const currentUserId = typeof userInfo.id === "string" ? parseInt(userInfo.id) : userInfo.id;
			const isCreator = adjustment.proposedBy === currentUserId || adjustment.createdBy === currentUserId;

			// Chỉ hiển thị nút Duyệt và Từ chối nếu user hiện tại KHÔNG phải là người tạo
			if (!isCreator) {
				actions.push({
					text: "Duyệt",
					color: "#10B981",
					onClick: () => handleApproveAdjustmentDirect(adjustment.id),
				});
				actions.push({
					text: "Từ chối",
					color: "#EF4444",
					onClick: () => handleRejectAdjustmentDirect(adjustment.id),
				});
			}

			// Chỉ hiển thị nút Hủy nếu user hiện tại là người tạo
			if (isCreator) {
				actions.push({
					text: "Hủy",
					color: "#EF4444",
					onClick: () => handleCancelClick(adjustment.id),
				});
			}
		}

		actions.push({
			text: "Chi tiết",
			color: "#005E9E",
			onClick: () => handleAdjustmentDetail(adjustment),
		});

		return actions;
	};

	const handleAdjustmentDetail = async (adjustment: SettlementAdjustment) => {
		try {
			// Load lại detail từ API để có đầy đủ thông tin reviewer
			const detail = await getAdjustmentDetail(adjustment.id);
			setSelectedAdjustment(detail);
			setIsAdjustmentModalOpen(true);
		} catch (error) {
			console.error("Error loading adjustment detail:", error);
			// Fallback: dùng adjustment từ list nếu load detail thất bại
			setSelectedAdjustment(adjustment);
			setIsAdjustmentModalOpen(true);
		}
	};

	const handleCloseAdjustmentModal = () => {
		setIsAdjustmentModalOpen(false);
	};

	const handleApproveAdjustment = () => {
		if (!selectedAdjustment) return;

		setPendingAdjustmentId(selectedAdjustment.id);
		setIsApproveConfirmModalOpen(true);
	};

	const handleRejectAdjustment = () => {
		if (!selectedAdjustment) return;

		// Đóng modal chi tiết
		setIsAdjustmentModalOpen(false);

		// Mở modal từ chối bước 1
		const valuePrefix = selectedAdjustment.type === 10 ? "+" : "-";
		const amount = `${valuePrefix}${formatNumber(selectedAdjustment.amount)} ${cycleCurrency}`;

		setRejectItemId(selectedAdjustment.id);
		setRejectData({ amount, reason: "" });
		setIsRejectModalOpen(true);
	};

	const handleCancelClick = (itemId: number) => {
		setCancelItemId(itemId);
		setIsCancelModalOpen(true);
	};

	const handleCancelConfirm = async () => {
		if (!cancelItemId) return;

		try {
			await cancelAdjustment(cancelItemId, "");

			// Reload adjustments và cycle detail
			if (cycleId) {
				const updatedDetail = await getSettlementCycleDetail(cycleId);
				setCycleDetail(updatedDetail);
				await fetchAdjustments();
			}

			alert("Hủy điều chỉnh thành công!");
			setIsCancelModalOpen(false);
			setCancelItemId(null);
		} catch (error) {
			console.error("Error canceling adjustment:", error);
			alert("Có lỗi xảy ra khi hủy điều chỉnh. Vui lòng thử lại.");
		}
	};

	const handleCancelClose = () => {
		setIsCancelModalOpen(false);
		setCancelItemId(null);
	};

	const handleAddAdjustment = () => {
		setIsAddAdjustmentModalOpen(true);
	};

	const handleCloseAddAdjustmentModal = () => {
		setIsAddAdjustmentModalOpen(false);
	};

	const handleSubmitAdjustment = async (data: any) => {
		if (!cycleId || !userInfo?.id) {
			console.error("Missing cycleId or userInfo");
			alert("Thiếu thông tin người dùng. Vui lòng đăng nhập lại.");
			return;
		}

		if (!data.reasonCode || data.reasonCode.trim() === "") {
			alert("Vui lòng chọn mã lý do.");
			return;
		}

		const amount = parseFloat(data.amount) || 0;
		if (amount <= 0) {
			alert("Số tiền phải lớn hơn 0.");
			return;
		}

		try {
			const adjustmentType = parseInt(data.adjustmentType) || 10;
			let attachments: string[] = [];
			if (data.files && Array.isArray(data.files) && data.files.length > 0) {
				try {
					const uploadedUrls = await uploadFiles(data.files);
					attachments = uploadedUrls;
				} catch (uploadError) {
					console.error("Error uploading files:", uploadError);
					alert("Có lỗi xảy ra khi upload file. Vui lòng thử lại.");
					return;
				}
			}

			const adjustmentData = {
				createdBy: typeof userInfo.id === "string" ? parseInt(userInfo.id) : userInfo.id,
				proposedBy: typeof userInfo.id === "string" ? parseInt(userInfo.id) : userInfo.id,
				type: adjustmentType,
				amount: amount,
				reasonCode: data.reasonCode.trim(),
				description: data.description || "",
				attachments: attachments,
			};

			await createSettlementAdjustment(cycleId, adjustmentData);

			// Sau khi thêm điều chỉnh thành công:
			// - Clear filter (về "all")
			// - Reset page về 1
			setSelectedReason("all");
			setSelectedStatus("all");
			setAdjustmentPage(1);

			const updatedDetail = await getSettlementCycleDetail(cycleId);
			setCycleDetail(updatedDetail);

			// Reload adjustments với filter đã được clear (page 1, no filter)
			setAdjustmentLoading(true);
			try {
				const sort: any = adjustmentSortField
					? {
							[adjustmentSortField]: adjustmentSortOrder,
					  }
					: { id: "DESC" as const };

				const result = await getSettlementAdjustments(
					1,
					10,
					cycleId,
					undefined, // no status filter
					undefined, // no reason filter
					sort
				);

				setAdjustmentData(result.data);
				setAdjustmentTotal(result.total);
				setAdjustmentPages(result.pages);
			} catch (error) {
				console.error("Error fetching adjustments:", error);
			} finally {
				setAdjustmentLoading(false);
			}

			setIsAddAdjustmentModalOpen(false);
			setIsSuccessModalOpen(true);
		} catch (error) {
			console.error("Error creating adjustment:", error);
			alert("Có lỗi xảy ra khi tạo điều chỉnh. Vui lòng thử lại.");
		}
	};

	const handleAddBooking = () => {
		setIsAddBookingModalOpen(true);
	};

	const handleCloseAddBookingModal = () => {
		setIsAddBookingModalOpen(false);
	};

	const handleSubmitBooking = async (data: any) => {
		if (!cycleId) {
			alert("Thiếu thông tin cycleId. Vui lòng thử lại.");
			return;
		}

		try {
			// Convert date format từ YYYY-MM-DD sang format backend yêu cầu
			const dropoffDate = data.dropoff ? new Date(data.dropoff + "T00:00:00") : null;
			const pickupDate = data.pickup ? new Date(data.pickup + "T00:00:00") : null;

			if (!dropoffDate || !pickupDate) {
				alert("Vui lòng chọn đầy đủ ngày drop-off và pick-up.");
				return;
			}

			const bookingData = {
				customerEmail: data.email.trim(),
				customerName: data.customerName.trim(),
				amount: parseFloat(data.amount) || 0,
				timeIn: dropoffDate.toISOString(),
				timeOut: pickupDate.toISOString(),
				quantity: parseInt(data.quantity) || 1,
			};

			await createBooking(cycleId, bookingData);

			// Reload cycle detail và booking items
			const updatedDetail = await getSettlementCycleDetail(cycleId);
			setCycleDetail(updatedDetail);

			// Reload booking items
			const sort: any = {};
			if (bookingSortField) {
				sort[bookingSortField] = bookingSortOrder;
			}

			const bookingResponse = await getSettlementItems(
				bookingPage,
				10,
				cycleId,
				undefined,
				undefined,
				selectedStatus === "all" ? undefined : parseInt(selectedStatus),
				searchTerm || undefined,
				Object.keys(sort).length > 0 ? sort : undefined
			);

			const mappedItems = bookingResponse.data.map((item: any) => {
				const statusColors = getItemStatusColor(item.status);
				return {
					id: item.id,
					booking: String(item.id),
					customer: item.customerName,
					email: item.customerEmail,
					dropoff: formatDate(item.timeIn),
					pickup: formatDate(item.timeOut),
					qty: String(item.quantity),
					income: formatNumber(item.finalAmount),
					type: getItemTypeText(item.type),
					typeBg: "#DBEAFE",
					typeColor: "#1E40AF",
					status: getItemStatusText(item.status),
					statusBg: statusColors.bg,
					statusColor: statusColors.text,
					orderId: item.orderId,
				};
			});

			setBookingItems(mappedItems);
			setBookingTotal(bookingResponse.total);
			setBookingPages(bookingResponse.pages);

			setIsAddBookingModalOpen(false);
			setIsBookingSuccessModalOpen(true);
		} catch (error: any) {
			console.error("Error creating booking:", error);
			const errorMessage = error?.response?.data?.message || "Có lỗi xảy ra khi tạo booking. Vui lòng thử lại.";
			alert(errorMessage);
		}
	};

	const handleBookingDetail = (bookingId?: string, bookingItemId?: number) => {
		// Tìm booking item từ raw data
		const bookingItem = rawBookingItems.find((item) => item.id === bookingItemId);
		setSelectedBookingId(bookingId || null);
		setSelectedBookingItemId(bookingItemId || null);
		setSelectedBookingItem(bookingItem || null);
		setIsBookingDetailModalOpen(true);
	};

	const handleCloseBookingDetailModal = () => {
		setIsBookingDetailModalOpen(false);
		setSelectedBookingId(null);
		setSelectedBookingItemId(null);
		setSelectedBookingItem(null);
	};

	const handleApproveBooking = (id: string) => {
		setPendingBookingId(id);
		setIsApproveConfirmModalOpen(true);
	};

	const handleConfirmApproveBooking = async () => {
		if (!pendingBookingId) return;

		try {
			// id có thể là adjustment id từ transaction log, nhưng API cần booking item id
			// Nếu có selectedBookingItemId thì dùng nó, nếu không thì parse id
			const bookingItemId = selectedBookingItemId || parseInt(pendingBookingId);

			if (!bookingItemId || isNaN(bookingItemId)) {
				openAlertDialog(
					AlertType.Fail,
					"Không tìm thấy booking item ID. Vui lòng thử lại.",
					undefined,
					undefined,
					undefined,
					{ title: "Lỗi" }
				);
				return;
			}

			await confirmBooking(bookingItemId);

			// Reload booking items và cycle detail
			if (cycleId) {
				const updatedDetail = await getSettlementCycleDetail(cycleId);
				setCycleDetail(updatedDetail);

				// Reload booking items
				const sort: any = {};
				if (bookingSortField) {
					sort[bookingSortField] = bookingSortOrder;
				} else {
					sort.id = "DESC";
				}

				const response = await getSettlementItems(
					bookingPage,
					10,
					cycleId,
					undefined,
					undefined,
					selectedStatus === "all" ? undefined : parseInt(selectedStatus),
					searchTerm || undefined,
					sort
				);

				const mappedItems = response.data.map((item: any) => {
					const statusColors = getItemStatusColor(item.status);
					return {
						id: item.id,
						booking: String(item.id),
						customer: item.customerName,
						email: item.customerEmail,
						dropoff: formatDate(item.timeIn),
						pickup: formatDate(item.timeOut),
						qty: String(item.quantity),
						income: formatNumber(item.finalAmount),
						type: getItemTypeText(item.type),
						typeBg: "#DBEAFE",
						typeColor: "#1E40AF",
						status: getItemStatusText(item.status),
						statusBg: statusColors.bg,
						statusColor: statusColors.text,
						orderId: item.orderId,
					};
				});

				setBookingItems(mappedItems);
				setBookingTotal(response.total);
				setBookingPages(response.pages);
			}

			setIsBookingDetailModalOpen(false);
			setIsApproveSuccessModalOpen(true);
		} catch (error: any) {
			console.error("Error confirming booking:", error);
			let errorMessage = "Có lỗi xảy ra khi duyệt booking. Vui lòng thử lại.";

			if (error?.response?.data?.message) {
				errorMessage = error.response.data.message;
			} else if (error?.message) {
				errorMessage = error.message;
			}

			// Kiểm tra nếu là lỗi NotFound (booking không tồn tại hoặc không ở trạng thái đúng)
			if (errorMessage.includes("not found") || errorMessage.includes("Some thing gone wrong")) {
				errorMessage =
					'Booking này không thể duyệt. Có thể booking không tồn tại hoặc không ở trạng thái "Chờ duyệt".';
			}

			openAlertDialog(AlertType.Fail, errorMessage, undefined, undefined, undefined, { title: "Lỗi" });
		}
	};

	const handleRejectBooking = (id: string) => {
		setPendingRejectBookingId(id);
		setIsRejectBookingModalOpen(true);
	};

	const handleConfirmRejectBooking = async (reason: string) => {
		if (!pendingRejectBookingId) return;

		try {
			// id có thể là adjustment id từ transaction log, nhưng API cần booking item id
			// Nếu có selectedBookingItemId thì dùng nó, nếu không thì parse id
			const bookingItemId = selectedBookingItemId || parseInt(pendingRejectBookingId);

			if (!bookingItemId || isNaN(bookingItemId)) {
				openAlertDialog(
					AlertType.Fail,
					"Không tìm thấy booking item ID. Vui lòng thử lại.",
					undefined,
					undefined,
					undefined,
					{ title: "Lỗi" }
				);
				return;
			}

			await rejectBooking(bookingItemId, reason);

			// Reload booking items và cycle detail
			if (cycleId) {
				const updatedDetail = await getSettlementCycleDetail(cycleId);
				setCycleDetail(updatedDetail);

				// Reload booking items
				const sort: any = {};
				if (bookingSortField) {
					sort[bookingSortField] = bookingSortOrder;
				} else {
					sort.id = "DESC";
				}

				const response = await getSettlementItems(
					bookingPage,
					10,
					cycleId,
					undefined,
					undefined,
					selectedStatus === "all" ? undefined : parseInt(selectedStatus),
					searchTerm || undefined,
					sort
				);

				const mappedItems = response.data.map((item: any) => {
					const statusColors = getItemStatusColor(item.status);
					return {
						id: item.id,
						booking: String(item.id),
						customer: item.customerName,
						email: item.customerEmail,
						dropoff: formatDate(item.timeIn),
						pickup: formatDate(item.timeOut),
						qty: String(item.quantity),
						income: formatNumber(item.finalAmount),
						type: getItemTypeText(item.type),
						typeBg: "#DBEAFE",
						typeColor: "#1E40AF",
						status: getItemStatusText(item.status),
						statusBg: statusColors.bg,
						statusColor: statusColors.text,
						orderId: item.orderId,
					};
				});

				setBookingItems(mappedItems);
				setBookingTotal(response.total);
				setBookingPages(response.pages);
			}

			setIsBookingDetailModalOpen(false);
			// Delay để đảm bảo modal từ chối đóng trước khi mở modal thành công
			setTimeout(() => {
				setIsRejectBookingSuccessModalOpen(true);
			}, 200);
		} catch (error: any) {
			console.error("Error rejecting booking:", error);
			let errorMessage = "Có lỗi xảy ra khi từ chối booking. Vui lòng thử lại.";

			if (error?.response?.data?.message) {
				errorMessage = error.response.data.message;
			} else if (error?.message) {
				errorMessage = error.message;
			}

			// Kiểm tra nếu là lỗi NotFound (booking không tồn tại hoặc không ở trạng thái đúng)
			if (errorMessage.includes("not found") || errorMessage.includes("Some thing gone wrong")) {
				errorMessage =
					'Booking này không thể từ chối. Có thể booking không tồn tại hoặc không ở trạng thái "Chờ duyệt".';
			}

			openAlertDialog(AlertType.Fail, errorMessage, undefined, undefined, undefined, { title: "Lỗi" });
		}
	};
	return (
		<MyBookingWrapper>
			<OrderInner>
				<DebtDetailBreadcrumb>
					<BreadcrumbItem>
						<Link
							to="/"
							style={{ textDecoration: "none", color: "inherit" }}>
							Home
						</Link>
					</BreadcrumbItem>
					<BreadcrumbSeparator
						src="./static/img/direction-right.png"
						alt=">"
					/>
					<BreadcrumbItem>
						<Link to="/debt-management">Quản lý công nợ</Link>
					</BreadcrumbItem>
					<BreadcrumbSeparator
						src="./static/img/direction-right.png"
						alt=">"
					/>
					<BreadcrumbItem active>Chi tiết công nợ</BreadcrumbItem>
				</DebtDetailBreadcrumb>
			</OrderInner>

			<DebtDetailContainer>
				<DebtDetailHeader>
					<div>
						<DebtDetailTitle>
							{loading
								? "Đang tải..."
								: cycleDetail
								? `Công nợ tháng ${cycleDetail.periodMonth}/${cycleDetail.periodYear}`
								: "Công nợ"}
						</DebtDetailTitle>
						<DebtDetailSubtitle>{cycleDetail?.partner?.name || "Chưa có thông tin"}</DebtDetailSubtitle>
					</div>
				</DebtDetailHeader>

				{loading ? (
					<DebtDetailSummaryCards>
						{/* Hiển thị loading state hoặc không hiển thị gì */}
					</DebtDetailSummaryCards>
				) : summaryData.length > 0 ? (
					<DebtDetailSummaryCards>
						{summaryData.map((item, index) => (
							<DebtDetailCard
								key={index}
								onMouseEnter={() => setHoveredCardIndex(index)}
								onMouseLeave={() => setHoveredCardIndex(null)}>
								<DebtDetailCardIcon color={item.iconBg}>
									<img
										src={hoveredCardIndex === index ? item.iconHover : item.icon}
										alt={item.title}
										style={{ width: "42px", height: "42px" }}
									/>
								</DebtDetailCardIcon>
								<DebtDetailCardContent>
									<DebtDetailCardText>{item.title}</DebtDetailCardText>
									<DebtDetailCardValue color={item.valueColor}>{item.value}</DebtDetailCardValue>
								</DebtDetailCardContent>
							</DebtDetailCard>
						))}
					</DebtDetailSummaryCards>
				) : null}

				<DebtDetailSection>
					<DebtDetailSectionHeader>
						<DebtDetailSectionTitle>
							Lịch sử điều chỉnh cấp công nợ ({adjustmentTotal || 0})
						</DebtDetailSectionTitle>
						<DebtDetailSectionActions>
							<DebtDetailFilterRow>
								<DebtDetailSelect
									value={selectedReason}
									onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
										setSelectedReason(e.target.value)
									}>
									<option value="all">Chọn lý do điều chỉnh</option>
									{getAllAdjustmentReasonCodes().map((reason) => (
										<option
											key={reason.value}
											value={reason.value}>
											{reason.value}
										</option>
									))}
								</DebtDetailSelect>
								<DebtDetailSelect
									value={selectedStatus}
									onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
										setSelectedStatus(e.target.value)
									}>
									<option value="all">Tất cả trạng thái</option>
									<option value="pending">Chờ duyệt</option>
									<option value="approved">Đã duyệt</option>
									<option value="rejected">Đã từ chối</option>
								</DebtDetailSelect>
								<PermissionGate permission="settlements.manage">
									<DebtDetailAddButton onClick={handleAddAdjustment}>
										+ Thêm điều chỉnh
									</DebtDetailAddButton>
								</PermissionGate>
							</DebtDetailFilterRow>
						</DebtDetailSectionActions>
					</DebtDetailSectionHeader>

					<DebtDetailTable>
						<DebtDetailTableHeader>
							<DebtDetailTableCell
								style={{ cursor: "pointer" }}
								onClick={() => handleAdjustmentSort("id")}>
								NGUỒN
								{adjustmentSortField === "id" && (
									<img
										src="./static/img/debt/arrow.png"
										alt="Sort"
										style={{
											width: "12px",
											height: "12px",
											marginLeft: "4px",
											transform: adjustmentSortOrder === "ASC" ? "rotate(180deg)" : "none",
										}}
									/>
								)}
								{adjustmentSortField !== "id" && (
									<img
										src="./static/img/debt/arrow.png"
										alt="Sort"
										style={{ width: "12px", height: "12px", marginLeft: "4px", opacity: 0.3 }}
									/>
								)}
							</DebtDetailTableCell>
							<DebtDetailTableCell
								style={{ cursor: "pointer" }}
								onClick={() => handleAdjustmentSort("amount")}>
								GIÁ TRỊ
								{adjustmentSortField === "amount" && (
									<img
										src="./static/img/debt/arrow.png"
										alt="Sort"
										style={{
											width: "12px",
											height: "12px",
											marginLeft: "4px",
											transform: adjustmentSortOrder === "ASC" ? "rotate(180deg)" : "none",
										}}
									/>
								)}
								{adjustmentSortField !== "amount" && (
									<img
										src="./static/img/debt/arrow.png"
										alt="Sort"
										style={{ width: "12px", height: "12px", marginLeft: "4px", opacity: 0.3 }}
									/>
								)}
							</DebtDetailTableCell>
							<DebtDetailTableCell>LÝ DO</DebtDetailTableCell>
							<DebtDetailTableCell
								style={{ cursor: "pointer" }}
								onClick={() => handleAdjustmentSort("status")}>
								TRẠNG THÁI
								{adjustmentSortField === "status" && (
									<img
										src="./static/img/debt/arrow.png"
										alt="Sort"
										style={{
											width: "12px",
											height: "12px",
											marginLeft: "4px",
											transform: adjustmentSortOrder === "ASC" ? "rotate(180deg)" : "none",
										}}
									/>
								)}
								{adjustmentSortField !== "status" && (
									<img
										src="./static/img/debt/arrow.png"
										alt="Sort"
										style={{ width: "12px", height: "12px", marginLeft: "4px", opacity: 0.3 }}
									/>
								)}
							</DebtDetailTableCell>
							<DebtDetailTableCell
								style={{ cursor: "pointer" }}
								onClick={() => handleAdjustmentSort("createdAt")}>
								THỜI GIAN
								{adjustmentSortField === "createdAt" && (
									<img
										src="./static/img/debt/arrow.png"
										alt="Sort"
										style={{
											width: "12px",
											height: "12px",
											marginLeft: "4px",
											transform: adjustmentSortOrder === "ASC" ? "rotate(180deg)" : "none",
										}}
									/>
								)}
								{adjustmentSortField !== "createdAt" && (
									<img
										src="./static/img/debt/arrow.png"
										alt="Sort"
										style={{ width: "12px", height: "12px", marginLeft: "4px", opacity: 0.3 }}
									/>
								)}
							</DebtDetailTableCell>
							<DebtDetailTableCell>HÀNH ĐỘNG</DebtDetailTableCell>
						</DebtDetailTableHeader>
						{adjustmentLoading ? (
							<DebtDetailTableRow>
								<div style={{ textAlign: "center", padding: "20px", gridColumn: "1 / -1" }}>
									Đang tải dữ liệu...
								</div>
							</DebtDetailTableRow>
						) : adjustmentData.length === 0 ? (
							<DebtDetailTableRow>
								<div style={{ textAlign: "center", padding: "20px", gridColumn: "1 / -1" }}>
									Không có dữ liệu
								</div>
							</DebtDetailTableRow>
						) : (
							adjustmentData.map((item) => {
								const source = getAdjustmentSource(item);
								const statusInfo = getAdjustmentStatusInfo(item.status);
								const action = getAdjustmentAction(item);
								const valueColor = item.type === 10 ? "#10B981" : "#EF4444";
								const valuePrefix = item.type === 10 ? "+" : "-";
								const backgroundColorMap: { [key: string]: string } = {
									Whalelo: "#DBEAFE",
									Station: "#ECFDF3",
									System: "#FFEDD5",
								};

								return (
									<DebtDetailTableRow key={item.id}>
										<DebtDetailTableCell>
											<div
												style={{
													display: "flex",
													alignItems: "center",
													gap: "8px",
													backgroundColor: backgroundColorMap[source.name] || "#E5E7EB",
													padding: "8px 12px",
													borderRadius: "20px",
													width: "fit-content",
												}}>
												<div
													style={{
														width: "8px",
														height: "8px",
														borderRadius: "50%",
														backgroundColor: source.color,
													}}></div>
												<span style={{ color: source.color, fontWeight: "500" }}>
													{source.name}
												</span>
											</div>
										</DebtDetailTableCell>
										<DebtDetailTableCell>
											<span style={{ color: valueColor, fontWeight: "500" }}>
												{valuePrefix}
												{formatNumber(item.amount)} {cycleCurrency}
											</span>
										</DebtDetailTableCell>
										<DebtDetailTableCell>{item.reasonCode || "N/A"}</DebtDetailTableCell>
										<DebtDetailTableCell>
											<DebtDetailStatusBadge
												backgroundColor={statusInfo.bg}
												textColor={statusInfo.color}>
												{statusInfo.text}
											</DebtDetailStatusBadge>
										</DebtDetailTableCell>
										<DebtDetailTableCell>
											{formatAdjustmentTime(item.createdAt)}
										</DebtDetailTableCell>
										<DebtDetailTableCell>
											<div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
												{action.map((act, idx) =>
													act.text === "Hủy" ||
													act.text === "Duyệt" ||
													act.text === "Từ chối" ? (
														<DebtDetailActionButton
															key={idx}
															backgroundColor={
																act.text === "Duyệt" ? "#10B981" : "#EF4444"
															}
															textColor="#FFFFFF"
															onClick={act.onClick}>
															{act.text}
														</DebtDetailActionButton>
													) : (
														<DebtDetailDetailsLink
															key={idx}
															onClick={act.onClick}>
															{act.text}
														</DebtDetailDetailsLink>
													)
												)}
											</div>
										</DebtDetailTableCell>
									</DebtDetailTableRow>
								);
							})
						)}
					</DebtDetailTable>

					<PaginationWrapperTop>
						<NumberPaginationBox
							page={adjustmentPage}
							count={adjustmentTotal}
							per={10}
							onChange={(val) => {
								setAdjustmentPage(val);
							}}
						/>
					</PaginationWrapperTop>
				</DebtDetailSection>

				<DebtDetailSection>
					<DebtDetailSectionHeader>
						<DebtDetailSectionTitle>Booking ({bookingTotal || 0})</DebtDetailSectionTitle>
						<DebtDetailSectionActions>
							<DebtDetailFilterRow>
								<DebtDetailSearchInput
									type="text"
									placeholder="Tìm theo mã booking, Khách hàng"
									value={searchTerm}
									onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
								/>
								<PermissionGate permission="settlements.manage">
									<DebtDetailAddButton onClick={handleAddBooking}>+ Thêm Booking</DebtDetailAddButton>
								</PermissionGate>
							</DebtDetailFilterRow>
						</DebtDetailSectionActions>
					</DebtDetailSectionHeader>

					<DebtDetailTable>
						<DebtDetailTableHeader isBookingTable={true}>
							<DebtDetailTableCell
								onClick={() => handleBookingSort("id")}
								style={{ cursor: "pointer", userSelect: "none" }}>
								BOOKING
								{bookingSortField === "id" && (
									<img
										src="./static/img/debt/arrow.png"
										alt="Sort"
										style={{
											width: "12px",
											height: "12px",
											marginLeft: "4px",
											transform: bookingSortOrder === "ASC" ? "rotate(180deg)" : "none",
										}}
									/>
								)}
								{bookingSortField !== "id" && (
									<img
										src="./static/img/debt/arrow.png"
										alt="Sort"
										style={{ width: "12px", height: "12px", marginLeft: "4px", opacity: 0.3 }}
									/>
								)}
							</DebtDetailTableCell>
							<DebtDetailTableCell>CUSTOMER</DebtDetailTableCell>
							<DebtDetailTableCell
								onClick={() => handleBookingSort("timeIn")}
								style={{ cursor: "pointer", userSelect: "none" }}>
								DROP-OFF
								{bookingSortField === "timeIn" && (
									<img
										src="./static/img/debt/arrow.png"
										alt="Sort"
										style={{
											width: "12px",
											height: "12px",
											marginLeft: "4px",
											transform: bookingSortOrder === "ASC" ? "rotate(180deg)" : "none",
										}}
									/>
								)}
								{bookingSortField !== "timeIn" && (
									<img
										src="./static/img/debt/arrow.png"
										alt="Sort"
										style={{ width: "12px", height: "12px", marginLeft: "4px", opacity: 0.3 }}
									/>
								)}
							</DebtDetailTableCell>
							<DebtDetailTableCell
								onClick={() => handleBookingSort("timeOut")}
								style={{ cursor: "pointer", userSelect: "none" }}>
								PICK-UP
								{bookingSortField === "timeOut" && (
									<img
										src="./static/img/debt/arrow.png"
										alt="Sort"
										style={{
											width: "12px",
											height: "12px",
											marginLeft: "4px",
											transform: bookingSortOrder === "ASC" ? "rotate(180deg)" : "none",
										}}
									/>
								)}
								{bookingSortField !== "timeOut" && (
									<img
										src="./static/img/debt/arrow.png"
										alt="Sort"
										style={{ width: "12px", height: "12px", marginLeft: "4px", opacity: 0.3 }}
									/>
								)}
							</DebtDetailTableCell>
							<DebtDetailTableCell
								onClick={() => handleBookingSort("quantity")}
								style={{ cursor: "pointer", userSelect: "none" }}
								alignRight>
								QTY
								{bookingSortField === "quantity" && (
									<img
										src="./static/img/debt/arrow.png"
										alt="Sort"
										style={{
											width: "12px",
											height: "12px",
											marginLeft: "4px",
											transform: bookingSortOrder === "ASC" ? "rotate(180deg)" : "none",
										}}
									/>
								)}
								{bookingSortField !== "quantity" && (
									<img
										src="./static/img/debt/arrow.png"
										alt="Sort"
										style={{ width: "12px", height: "12px", marginLeft: "4px", opacity: 0.3 }}
									/>
								)}
							</DebtDetailTableCell>
							<DebtDetailTableCell
								onClick={() => handleBookingSort("finalAmount")}
								style={{ cursor: "pointer", userSelect: "none" }}
								alignRight>
								INCOME
								{bookingSortField === "finalAmount" && (
									<img
										src="./static/img/debt/arrow.png"
										alt="Sort"
										style={{
											width: "12px",
											height: "12px",
											marginLeft: "4px",
											transform: bookingSortOrder === "ASC" ? "rotate(180deg)" : "none",
										}}
									/>
								)}
								{bookingSortField !== "finalAmount" && (
									<img
										src="./static/img/debt/arrow.png"
										alt="Sort"
										style={{ width: "12px", height: "12px", marginLeft: "4px", opacity: 0.3 }}
									/>
								)}
							</DebtDetailTableCell>
							<DebtDetailTableCell
								onClick={() => handleBookingSort("type")}
								style={{ cursor: "pointer", userSelect: "none" }}>
								LOẠI
								{bookingSortField === "type" && (
									<img
										src="./static/img/debt/arrow.png"
										alt="Sort"
										style={{
											width: "12px",
											height: "12px",
											marginLeft: "4px",
											transform: bookingSortOrder === "ASC" ? "rotate(180deg)" : "none",
										}}
									/>
								)}
								{bookingSortField !== "type" && (
									<img
										src="./static/img/debt/arrow.png"
										alt="Sort"
										style={{ width: "12px", height: "12px", marginLeft: "4px", opacity: 0.3 }}
									/>
								)}
							</DebtDetailTableCell>
							<DebtDetailTableCell
								onClick={() => handleBookingSort("status")}
								style={{ cursor: "pointer", userSelect: "none" }}>
								TRẠNG THÁI
								{bookingSortField === "status" && (
									<img
										src="./static/img/debt/arrow.png"
										alt="Sort"
										style={{
											width: "12px",
											height: "12px",
											marginLeft: "4px",
											transform: bookingSortOrder === "ASC" ? "rotate(180deg)" : "none",
										}}
									/>
								)}
								{bookingSortField !== "status" && (
									<img
										src="./static/img/debt/arrow.png"
										alt="Sort"
										style={{ width: "12px", height: "12px", marginLeft: "4px", opacity: 0.3 }}
									/>
								)}
							</DebtDetailTableCell>
							<DebtDetailTableCell>CHI TIẾT</DebtDetailTableCell>
						</DebtDetailTableHeader>
						{bookingLoading ? (
							<DebtDetailTableRow isBookingTable={true}>
								<div style={{ textAlign: "center", padding: "20px", gridColumn: "1 / -1" }}>
									Đang tải dữ liệu...
								</div>
							</DebtDetailTableRow>
						) : bookingItems.length === 0 ? (
							<DebtDetailTableRow isBookingTable={true}>
								<div style={{ textAlign: "center", padding: "20px", gridColumn: "1 / -1" }}>
									Không có dữ liệu
								</div>
							</DebtDetailTableRow>
						) : (
							bookingItems.map((item) => (
								<DebtDetailTableRow
									key={item.id}
									isBookingTable={true}>
									<DebtDetailTableCell>{item.booking}</DebtDetailTableCell>
									<DebtDetailTableCell>
										<div>
											<div style={{ fontWeight: "500", marginBottom: "4px" }}>
												{item.customer}
											</div>
											<div style={{ fontSize: "12px", color: "#6B7280" }}>{item.email}</div>
										</div>
									</DebtDetailTableCell>
									<DebtDetailTableCell>{item.dropoff}</DebtDetailTableCell>
									<DebtDetailTableCell>{item.pickup}</DebtDetailTableCell>
									<DebtDetailTableCell alignRight>{item.qty}</DebtDetailTableCell>
									<DebtDetailTableCell alignRight>{item.income}</DebtDetailTableCell>
									<DebtDetailTableCell>
										<DebtDetailStatusBadge
											backgroundColor={item.typeBg}
											textColor={item.typeColor}>
											{item.type}
										</DebtDetailStatusBadge>
									</DebtDetailTableCell>
									<DebtDetailTableCell>
										<DebtDetailStatusBadge
											backgroundColor={item.statusBg}
											textColor={item.statusColor}>
											{item.status}
										</DebtDetailStatusBadge>
									</DebtDetailTableCell>
									<DebtDetailTableCell>
										<DebtDetailDetailsLink
											onClick={() => handleBookingDetail(item.booking, item.id)}>
											Chi tiết{" "}
											<img
												src="./static/img/debt/arrow-right.png"
												alt="Arrow"
												style={{ width: "6.5px", height: "12px", marginLeft: "4px" }}
											/>
										</DebtDetailDetailsLink>
									</DebtDetailTableCell>
								</DebtDetailTableRow>
							))
						)}
					</DebtDetailTable>

					<PaginationWrapperTop>
						<NumberPaginationBox
							page={bookingPage}
							count={bookingTotal}
							per={10}
							onChange={(val) => setBookingPage(val)}
						/>
					</PaginationWrapperTop>
				</DebtDetailSection>
			</DebtDetailContainer>

			<AdjustmentDetailModal
				isOpen={isAdjustmentModalOpen}
				adjustment={selectedAdjustment}
				currency={cycleCurrency}
				onClose={handleCloseAdjustmentModal}
				onApprove={
					selectedAdjustment && selectedAdjustment.status === 10 && userInfo?.id
						? (() => {
								const currentUserId =
									typeof userInfo.id === "string" ? parseInt(userInfo.id) : userInfo.id;
								const isCreator =
									selectedAdjustment.proposedBy === currentUserId ||
									selectedAdjustment.createdBy === currentUserId;
								// Chỉ hiển thị nút duyệt nếu user hiện tại KHÔNG phải là người tạo
								return !isCreator ? handleApproveAdjustment : undefined;
						  })()
						: undefined
				}
				onReject={
					selectedAdjustment && selectedAdjustment.status === 10 && userInfo?.id
						? (() => {
								const currentUserId =
									typeof userInfo.id === "string" ? parseInt(userInfo.id) : userInfo.id;
								const isCreator =
									selectedAdjustment.proposedBy === currentUserId ||
									selectedAdjustment.createdBy === currentUserId;
								// Chỉ hiển thị nút từ chối nếu user hiện tại KHÔNG phải là người tạo
								return !isCreator ? handleRejectAdjustment : undefined;
						  })()
						: undefined
				}
			/>

			<CancelConfirmModal
				isOpen={isCancelModalOpen}
				onClose={handleCancelClose}
				onConfirm={handleCancelConfirm}
			/>

			<AddAdjustmentModal
				isOpen={isAddAdjustmentModalOpen}
				onClose={handleCloseAddAdjustmentModal}
				onSubmit={handleSubmitAdjustment}
				currency={cycleCurrency}
			/>

			<AddBookingModal
				isOpen={isAddBookingModalOpen}
				onClose={handleCloseAddBookingModal}
				onSubmit={handleSubmitBooking}
				currency={cycleCurrency}
			/>

			<BookingDetailModal
				isOpen={isBookingDetailModalOpen}
				onClose={handleCloseBookingDetailModal}
				onApprove={handleApproveBooking}
				onReject={handleRejectBooking}
				bookingId={selectedBookingId || undefined}
				bookingItemId={selectedBookingItemId || undefined}
				bookingItem={selectedBookingItem || undefined}
				cycleId={cycleId || undefined}
				currency={cycleCurrency}
			/>

			<AddAdjustmentSuccessModal
				isOpen={isSuccessModalOpen}
				onClose={() => setIsSuccessModalOpen(false)}
				onAddAnother={() => {
					setIsSuccessModalOpen(false);
					setIsAddAdjustmentModalOpen(true);
				}}
			/>

			<AddBookingSuccessModal
				isOpen={isBookingSuccessModalOpen}
				onClose={() => setIsBookingSuccessModalOpen(false)}
				onAddAnother={() => {
					setIsBookingSuccessModalOpen(false);
					setIsAddBookingModalOpen(true);
				}}
			/>

			<RejectAdjustmentModal
				isOpen={isRejectModalOpen}
				onClose={() => {
					setIsRejectModalOpen(false);
					setRejectItemId(null);
					setRejectData(null);
				}}
				onNext={handleRejectNext}
				amount={rejectData?.amount || `0.00 ${cycleCurrency}`}
				currency={cycleCurrency}
			/>

			<AdjustmentProposalModal
				isOpen={isProposalModalOpen}
				onClose={() => {
					setIsProposalModalOpen(false);
					setRejectItemId(null);
					setRejectData(null);
				}}
				onBack={handleProposalBack}
				onSubmit={handleRejectAndPropose}
				originalAmount={rejectData?.amount || `0.00 ${cycleCurrency}`}
				rejectReason={rejectData?.reason || ""}
				currency={cycleCurrency}
			/>

			<RejectBookingModal
				isOpen={isRejectBookingModalOpen}
				onClose={() => {
					setIsRejectBookingModalOpen(false);
					setPendingRejectBookingId(null);
				}}
				onConfirm={async (reason) => {
					setIsRejectBookingModalOpen(false);
					await handleConfirmRejectBooking(reason);
				}}
			/>

			<RejectBookingSuccessModal
				isOpen={isRejectBookingSuccessModalOpen}
				onClose={() => setIsRejectBookingSuccessModalOpen(false)}
			/>

			<ApproveAdjustmentSuccessModal
				isOpen={isApproveSuccessModalOpen}
				onClose={() => setIsApproveSuccessModalOpen(false)}
			/>

			<ApproveConfirmModal
				isOpen={isApproveConfirmModalOpen}
				onClose={() => {
					setIsApproveConfirmModalOpen(false);
					setPendingAdjustmentId(null);
					setPendingBookingId(null);
				}}
				onConfirm={async () => {
					setIsApproveConfirmModalOpen(false);
					if (pendingAdjustmentId) {
						const adjustmentId = pendingAdjustmentId;
						setPendingAdjustmentId(null);
						await handleConfirmApproveAdjustment(adjustmentId);
						// Đóng modal chi tiết nếu đang mở
						if (isAdjustmentModalOpen) {
							setIsAdjustmentModalOpen(false);
							setSelectedAdjustment(null);
						}
					} else if (pendingBookingId) {
						const bookingId = pendingBookingId;
						setPendingBookingId(null);
						await handleConfirmApproveBooking();
					}
				}}
				message={
					pendingBookingId
						? "Bạn có chắc chắn muốn duyệt booking này?"
						: "Bạn có chắc chắn muốn duyệt điều chỉnh này?"
				}
			/>

			<CommonAlertDialog {...dialogState} />
		</MyBookingWrapper>
	);
}
